import React from 'react';
import { Check } from 'lucide-react';

/**
 * v2 chip-select group — lead-time / compliance chips. Selection is a USER
 * action, so selected chips fill solid cobalt (white text) per the mocks;
 * multi-select chips carry a check, single-select chips stay clean. Round
 * radius per token set.
 */
export interface ChipOption {
  value: string;
  label: string;
}

export interface ChipSelectProps {
  options: ChipOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** single = radio-style (lead time); multi = checkboxes (compliance) */
  mode?: 'single' | 'multi';
  /** Check icon on the selected chip; defaults on for multi (the context-question mock shows it on single too). */
  check?: boolean;
  label?: string;
  className?: string;
}

export const ChipSelect: React.FC<ChipSelectProps> = ({
  options,
  value,
  onChange,
  mode = 'multi',
  check,
  label,
  className = '',
}) => {
  const showCheck = check ?? mode === 'multi';
  const toggle = (v: string) => {
    if (mode === 'single') {
      onChange(value.includes(v) ? [] : [v]);
    } else {
      onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    }
  };

  return (
    <div className={`flex flex-col gap-s2 ${className}`}>
      {label && <span className="text-row-title text-ink">{label}</span>}
      <div className="flex flex-wrap gap-s2" role="group" aria-label={label}>
        {options.map((o) => {
          const selected = value.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(o.value)}
              className={
                'inline-flex h-7 items-center gap-s1 rounded-round border px-s4 text-body-sm font-semibold ' +
                'transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 ' +
                (selected
                  ? 'border-action bg-action text-white'
                  : 'border-line-strong bg-surface text-neutral-ink hover:border-action')
              }
            >
              {selected && showCheck && <Check className="h-[11px] w-[11px]" />}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ChipSelect;
